import './ToolDeleteModal.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { ButtonWithLoader } from '../../../../../../design/buttons/ButtonWithLoader';
import { S_Radio } from '../../../../../../design/inputs/radio/S_Radio';
import { S_Modal } from '../../../../../../design/modals/modal/S_Modal';
import { ToolDeleteStrategy } from '../../../projectDetails.models';

interface IToolDeleteModalProps {
  showModal: boolean;
  strategy: ToolDeleteStrategy;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  onStrategyChange: (strategy: ToolDeleteStrategy) => void;
  toolType: string;
  annotationsCount: number;
  objectsCount: number;
}

export const ToolDeleteModal = observer((props: IToolDeleteModalProps): ReactElement => {
  const { t } = useTranslator();

  const onStrategyChange = (data: ToolDeleteStrategy) => (): void => {
    props.onStrategyChange(data);
  };

  return (
    <S_Modal
      isOpen={props.showModal}
      toggle={props.onCancel}
      headerText={t('confirm_delete')}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={props.onCancel}
          >
            {t('cancel')}
          </Button>
          <ButtonWithLoader
            onClick={() => {
              void props.onConfirm();
            }}
            loaderKey='tool-delete-confirm-button'
            disabled={props.strategy === ToolDeleteStrategy.None}
          >
            {t('delete')}
          </ButtonWithLoader>
        </>
      }
    >
      <div className='tool-delete-modal'>
        <div>
          {
            <Trans
              i18nKey={'tool_usage'}
              count={props.annotationsCount}
              values={{
                toolType: props.toolType,
                annotationsCount: props.annotationsCount.toString(),
                objectsCount: props.objectsCount.toString(),
              }}
              components={{ b: <b /> }}
            />
          }
        </div>
        <div>
          {
            <Trans
              i18nKey={'tool_usage_warning'}
              count={props.objectsCount}
              values={{
                toolType: props.toolType,
                annotationsCount: props.annotationsCount.toString(),
                objectsCount: props.objectsCount.toString(),
              }}
              components={{ b: <b /> }}
            />
          }
        </div>
        <div className='delete-tool-annotation-options'>
          <S_Radio
            id='option-leave'
            onChange={onStrategyChange(ToolDeleteStrategy.Leave)}
            checked={props.strategy === ToolDeleteStrategy.Leave}
            label={t('tool_delete_strategy_leave')}
          />
          <S_Radio
            id='option-review'
            onChange={onStrategyChange(ToolDeleteStrategy.MoveToReview)}
            checked={props.strategy === ToolDeleteStrategy.MoveToReview}
            label={t('tool_delete_strategy_review')}
          />
          <S_Radio
            id='option-draft'
            onChange={onStrategyChange(ToolDeleteStrategy.MoveToDraft)}
            checked={props.strategy === ToolDeleteStrategy.MoveToDraft}
            label={t('tool_delete_strategy_draft')}
          />
          <S_Radio
            id='option-discard'
            onChange={onStrategyChange(ToolDeleteStrategy.Discard)}
            checked={props.strategy === ToolDeleteStrategy.Discard}
            label={t('tool_delete_strategy_discard')}
          />
        </div>
        <p>{t('tool_delete_continue')}</p>
      </div>
    </S_Modal>
  );
});
